
import React, { useCallback, useState } from 'react';
import { Tabs, Image, Spin } from 'antd';
import CommonModal from "PhishingManager/components/Modal";
import { ModalProps } from "PhishingManager/components/Modal/Modal.types";
import InteractionPreviewImg from "../images/interaction-preview.png";
import CalculationImg from "../images/calculation.png";
import EvaluationImg from "../images/evalution.png";
import ProcessImg from "../images/process-preview.png";
import DetectImg from "../images/detect.png";
import RatingDescImg from "../images/rating-desc.png";
import CheckListImg from "../images/checklist.png";
import { Icon } from 'Components';
import useDownloadPDFFromURL from 'Hooks/useDownloadFileFromURL';
import { AUTOMATION_PDF_URL, INTERACTION_PDF_URL } from '../CampaignReport.constant';

const LearnMoreModal: React.FC<ModalProps> = ({
    open,
    onClose,
    onConfirm
}) => {
    const [activeTab, setActiveTab] = useState("interaction");

    const { downloadFile, loading } = useDownloadPDFFromURL();

    return (
        <CommonModal
            visible={open}
            confirmText="Close"
            cancelText={null}
            iconType={null}
            onClose={onClose}
            onConfirm={onConfirm}
            modalTitle={"Learn More"}
            parentClass='learn-more-modal'>
            <Spin spinning={loading}>
                <div className='modal-body'>
                    <div className='tabs-section'>
                        <Tabs activeKey={activeTab} onChange={setActiveTab}>
                            <Tabs.TabPane tab="Interpreting Results" key="interaction">
                                <InterpretResult />
                            </Tabs.TabPane>

                            <Tabs.TabPane tab="Automation Detection" key="automation">
                                <AutomationDetect />
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
                    <Icon type="download" className="icon-btn download-btn" onClick={() => downloadFile(activeTab === "interaction" ? INTERACTION_PDF_URL : AUTOMATION_PDF_URL, `${activeTab}-content`)} />
                </div>
            </Spin>
        </CommonModal>
    );
};

export default LearnMoreModal;

const InterpretResult = () => {
    return <div className="content-section" id="interaction-content">
        <div>
            <p>Venza Phishing™ simulations test employees' security parameters by presenting them with real-world examples of email-based social engineering attacks.</p>
            <p>Over a chosen period, each campaign distributes a set of email messages that link to a deceptive website to a group of employees. The site prompts recipients to enter sensitive information, such as login credentials, to gauge their response.</p>
            <p>After its conclusion, the campaign results are organized and distributed, along with recommendations for individual remediation and ongoing organizational training.</p>
        </div>

        <div>
            <h3>Understanding Interactions</h3>
            <p>Phishing simulations measure how users respond to potentially risky data security requests. The success of the response depends on how the user engages with the simulation.</p>
            <p>There are four categories of user responses to a Venza Phishing simulation:</p>
            <ul>
                <li>No Interaction – the user does not respond to the email, open the landing page, or submit data.</li>
                <li>Opened Email – the user responds by opening the email but does not click a link or submit data.</li>
                <li>Clicked Link –  the user opens the email and clicks an internal link but does not submit data on the campaign landing page.</li>
                <li>Submitted Data – the user opens the email, clicks an internal link, and shares sensitive data on the landing page.</li>
            </ul>
            <p className="mt-12">Of these interactions, No Interaction and Opened Email are "passing" responses. Clicked Link and Submitted Data are "failing" responses.</p>
            <Image src={InteractionPreviewImg} alt="interaction-preview" />
        </div>

        <div>
            <h3>Reading the Summary</h3>
            <p>The Summary tab provides high-level results from the Venza Phishing campaign, offering insights into your organization's security posture and individual employee risk</p>
            <p>The Executive Summary offers an overview of organizational campaign performance, including aggregate metrics of key campaign indicators.</p>
            <p>The list of Susceptible Employees includes unique users who interacted with a campaign by clicking a link or submitting data.</p>
            <p>The All Results section includes results from all users in the campaign, including both those with passing and failing results.</p>
            <p>The Pass Rate is the overall score. It shows, as a percentage, how many of your users responded appropriately to the simulation. A higher passing rate indicates stronger data security.</p>
            <p className="mt-12" style={{ marginBottom: "10px" }}>The Pass Rate is calculated as follows:</p>
            <Image src={CalculationImg} alt="calculation-preview" />
            <p>The Emails Sent, Opened Email, Clicked Link, and Submitted Data fields are numerical. They show the number of unique users that engaged in each interaction during the campaign.</p>
            <p>Note: The Email Opened number may be less than other interactions like Clicked Link due to email client limitations. For example, Email Opened uses tracking pixels that do not load under all settings.</p>
            <p>Multiple interactions (e.g., a user clicking a link several times) are counted as one instance for these fields.</p>
        </div>

        <div>
            <h3>Automated Interaction Detection</h3>
            <p>Modern email systems are complex and include security tools (such as spam filters) that often automatically interact with incoming email, regardless of user behavior (an “automated interaction”).</p>
            <p className="mt-12">Automated interactions are present in all phishing campaigns by any vendor, occur in most email platforms, and are not limited to any specific tool.</p>
            <Image src={ProcessImg} alt="process-preview" />
            <p>When undetected, automated interactions decrease the accuracy of a phishing campaign by, for example, incorrectly reporting a “sniffed” link as “clicked”.</p>
            <p>To prevent this, Venza Phishing™ uses a proprietary Automated Interaction Detection (AID) algorithm to identify software-based email interactions and distinguish them from user interactions.</p>
            <p>Details about algorithm criteria can be found on the Automation Checks tab of this report.</p>
            <p className="mt-12">The AID algorithm evaluates the Clicked Link and Submitted Data interactions. It does not evaluate the No Interaction and Email Opened interactions.</p>
            <Image src={EvaluationImg} alt="evalution-preview" />
            <p>Using finely-tuned rules based on factors such as geolocation, timing, and frequency, it analyses interactions and assigns them a probability rating of the likelihood that action was performed automatically and not by a human user.</p>
            <p>This probability rating can be found in the “Automated Interaction” column of the Advanced tab.</p>
            <p>Even with advanced detection tools, sourcing of interactions is inherently and inevitably probabilistic due to the breadth, complexity, and changing nature of automation.</p>
            <p>Because of this uncertainty, Venza recommends that clients err on the side of caution and treat each potential interaction as an opportunity for ongoing awareness raising.</p>
        </div>

        <div>
            <h3 style={{ marginBottom: "5px" }}>Property Level Results</h3>
            <p>The Property Level tab shows summary results information divided by property.</p>
            <p>It allows training managers to compare their Venza Phishing results across their portfolio to identify properties that are excelling and those who may benefit from additional training.</p>
            <p>The Pass Rate is the overall score. It shows, as a percentage, how many of your users responded appropriately to the simulation. A higher passing rate indicates stronger data security.</p>
            <p>The Emails Sent, Opened Email, Clicked Link, and Submitted Data fields are numerical. They show the number of users that engaged in each interaction during the campaign.</p>
        </div>

        <div>
            <h3>Advanced Findings</h3>
            <p>The Advanced tab includes all the data collected in the Venza Phishing campaign.</p>
            <p>For most clients, the high-level results on the Summary tab provide enough information to guide their future program efforts, so review of this information is not required.</p>
            <p>In some cases, however, it may be helpful to review a detailed record with additional information about individual users.</p>
            <p>Each row includes information for a different user. It reports key details such as timestamps, along with advanced data points like IP address, internet service provider, and user agent (browser and operating system) associated with the interaction.</p>
            <p>Multiple interactions are listed as unique events in this tab, even though they are combined when calculating the Pass Rate in the Executive Summary.</p>
            <p>This tab may have more row than emails sent. It records each interaction in a separate row, and users may interact with an email more than once.</p>
        </div>

        <div>
            <h3>Recommendations and Next Steps</h3>
            <p>To proactively address vulnerabilities, Venza recommends that Susceptible Employees are assigned supplemental phishing training.</p>
            <p>Venza offers a specific remediation course is tailored to the contours of this campaign, delivering guidance on missed red flags along with general email security best practices.</p>
            <p>For more comprehensive instruction, the Venza Learning™ Content Library includes additional courses, such as Phishing Defense, that address common hospitality email threats.</p>
            <p>Venza also offers a range of instructional resources to strengthen email security organization-wide, including microlessons, breakroom posters, visual aids, and more.</p>
            <p>For more information, contact your Customer Success Coach.</p>
        </div>
    </div>
}

const AutomationDetect = () => {
    return <div className="content-section" id="automation-content">
        <div>
            <p>When analyzing phishing simulation results, it’s important to distinguish between real user activity and automated systems that interact with emails.</p>
            <p>Automated activity can come from security tools, mail gateways, or bots that scan links for threats. These don’t reflect human behavior and can distort performance metrics if not properly identified.</p>
            <p>To address this, Venza uses a proprietary, weighted, dynamic algorithm to parse interactions and validate whether they were performed by a real user or by an automated system.</p>
            <p>Underwritten by advanced AI and machine learning, the Automated Interaction Detection (AID) Algorithm performs behavioral and technological profiling using over 100 variables.</p>
        </div>

        <div>
            <h3>How It Works</h3>
            <p className="mt-12">AID categorizes activity into two main types:</p>
            <Image src={DetectImg} alt="detect-preview" />
            <p>AID uses a two-step workflow. It first evaluates whether the interaction can be confidently linked to a human user. If not, it then checks for signs of automation.</p>
            <p className="mt-12">During each step, AID applies a set of tests and assigns a confidence rating based on the predictive strength of the signals observed.</p>
            <Image src={RatingDescImg} alt="rating-desc-preview" />
        </div>

        <div>
            <h3>Automation Checks</h3>
            <p className="mt-12">Examples of types of AID evaluations include:</p>
            <Image src={CheckListImg} alt="checklist-preview" />
        </div>
    </div>
}